import React, { useState } from 'react';
import { Button, Menu, MenuItem } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { LanguageAttributes } from '@loyalty/shared-common/gamification-api-client';
import { colorPalette } from '@qualifio/ui-commons';
import { createStyles, makeStyles } from '@mui/styles';
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons/faChevronDown';
import { useLanguages } from '../../languages/LanguagesContext';

interface StyleProps {
  anchorEl?: HTMLButtonElement;
}

const useStyles = makeStyles(() =>
  createStyles<any, StyleProps>({
    root: {
      display: 'flex',
      borderLeft: `1px solid ${colorPalette.grey.main}`,
    },
    button: {
      height: '100% !important',
    },
    menu: {
      borderTopRightRadius: 0,
      borderTopLeftRadius: 0,
      marginLeft: 1,
      minWidth: ({ anchorEl }) => anchorEl && anchorEl.getBoundingClientRect()?.width + 2,
    },
    menuList: {
      padding: 0,
    },
  }),
);

interface Props {
  onChange?: (language: LanguageAttributes) => void;
}

export function LanguageSwitcher({ onChange }: Props): React.ReactElement {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | undefined>(undefined);
  const classes = useStyles({ anchorEl });
  const { languages, currentLanguage } = useLanguages();

  const handleClick = ({ currentTarget }: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    setAnchorEl(currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(undefined);
  };

  const handleChange = (language: LanguageAttributes) => () => {
    handleClose();
    onChange?.(language);
  };

  return (
    <div className={classes.root}>
      <Button
        className={classes.button}
        variant="text"
        endIcon={<FontAwesomeIcon icon={faChevronDown} />}
        onClick={handleClick}
      >
        {currentLanguage?.name}
      </Button>
      <Menu
        classes={{ paper: classes.menu, list: classes.menuList }}
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
      >
        {languages.map((language: LanguageAttributes) => (
          <MenuItem
            key={language.isoCode}
            onClick={handleChange(language)}
            selected={currentLanguage?.isoCode === language.isoCode}
          >
            {language.name}
          </MenuItem>
        ))}
      </Menu>
    </div>
  );
}
